import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo } from 'react';
import { Grid, Paper, styled, Tab, Tabs } from '@mui/material';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import { PAGE_DOWN_CODE, PAGE_UP_CODE } from 'common/constants/general';
import { useStore } from 'common/store';
import { LayoutContextValues, NavItems } from 'common/types/store';
import LayoutContext from 'context/LayoutContext';
import { getArrayValue, getNumberValue, getStringValue } from 'utilities';
import { TabSubMenu } from './components';
import { HORIZONTAL_NAVIGATION_SELECTED_COLOR } from './styles';
import { HorizontalNavigationProps, NavigationParams } from './types';

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.primary[600],
}));

const StyledContainer = styled(Grid)(({ theme }) => ({
  margin: 0,
  overflowX: 'auto',
  padding: `0px ${theme.spacing(2)}`,
  width: '100%',
}));

const StyledMenuItems = styled(Grid)({
  padding: '0px !important',
});

const StyledTab = styled(Tab, {
  shouldForwardProp: prop => prop !== 'isHidden',
})<{ isHidden?: boolean }>(({ theme, isHidden }) => ({
  fontSize: theme.spacing(1.65),
  fontWeight: 500,
  minWidth: 0,
  padding: `${theme.spacing(1)} ${theme.spacing(2.5)} !important`,
  color: HORIZONTAL_NAVIGATION_SELECTED_COLOR,

  '&:hover': {
    color: theme.palette.common.white,
  },

  '&.Mui-disabled': {
    color: HORIZONTAL_NAVIGATION_SELECTED_COLOR,
  },

  '&::after': {
    content: '""',
    height: '3px',
    backgroundColor: theme.palette.primary.main,
    width: '100%',
    position: 'absolute',
    bottom: 0,
    left: 0,
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 200ms ease',
  },

  '&.Mui-selected': {
    color: theme.palette.common.white,
    '&::after': {
      transform: 'scaleX(1)',
    },
  },

  ...(isHidden && {
    display: 'none',
  }),
}));

const StyledTabsContainer = styled(Tabs)(() => ({
  '& .MuiTabs-indicator': { 
    display: 'none',
  },
}));

const StyledNavActions = styled(Grid)(() => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'flex-end',
}));

const HorizontalNavigation = (props: HorizontalNavigationProps) => {
  const { children } = props;

  const { navItems = [] } = useContext(LayoutContext) as unknown as LayoutContextValues;
  const store = useStore() ?? [];
  const [{ isShowLoadingProgress = false }] = store.length ? store : [{}];

  const { tableSlugParam, tabSlugParam } = useParams<NavigationParams>();
  const slugParam = useMemo(() => getStringValue(tableSlugParam ?? tabSlugParam), [tabSlugParam, tableSlugParam]);

  const selectedNavItem = useMemo(() => {
    if (slugParam && navItems && !isEmpty(navItems)) {
      const result = navItems?.find(navItem => navItem.slug === slugParam);

      if (result) {
        return result?.itemKey;
      }
    }

    return false;
  }, [navItems, slugParam]);

  const handleKeyup = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key || event.code;
      const initiatingKey = event.ctrlKey || event.metaKey || event.altKey;

      const lastItemIndex = getNumberValue(getArrayValue(navItems)?.length) - 1;
      const currentPosition = navItems?.findIndex(navItem => navItem.slug === slugParam) ?? -1;

      if (initiatingKey) {
        if (key === PAGE_DOWN_CODE && currentPosition < lastItemIndex) {
          navItems?.[currentPosition + 1]?.callback?.();
        } else if (key === PAGE_UP_CODE && currentPosition > 0) {
          navItems?.[currentPosition - 1]?.callback?.();
        }
      }
    },
    [navItems, slugParam]
  );

  // Adding event listener for keyboard navigation
  useEffect(() => {
    if (navItems) {
      document.addEventListener('keyup', handleKeyup);
    }

    return () => document.removeEventListener('keyup', handleKeyup);
  }, [handleKeyup, navItems, tableSlugParam]);

  const handleChange = (event: ChangeEvent<unknown>, selectedItem: NavItems['itemKey']): void => {
    const targetClass = (event as ChangeEvent<HTMLDivElement>)?.target?.classList;

    if (targetClass?.length && !targetClass?.contains('icon')) {
      // Execute the callback function of the selected item
      navItems?.find(navItem => navItem?.itemKey === selectedItem)?.callback?.();
    }
  };

  return (
    <StyledPaper elevation={0} id="horizontal-nav" square>
      <StyledContainer alignItems="center" container direction="row" justifyContent="space-between" wrap="nowrap">
        <StyledMenuItems item>
          {!isEmpty(navItems) && (
            <StyledTabsContainer
              aria-label="tab bar"
              id="horizontal-nav-tabs"
              indicatorColor="primary"
              onChange={handleChange}
              value={selectedNavItem}>
              {navItems?.map(navItem => {
                if (!isEmpty(navItem?.subMenu)) {
                  return (
                    <TabSubMenu
                      item={navItem}
                      onChange={handleChange}
                      key={`tab-${navItem?.id}`}
                      disabled={isShowLoadingProgress}
                    />
                  );
                }

                return (
                  <StyledTab
                    isHidden={navItem?.hidden}
                    disabled={isShowLoadingProgress ?? navItem?.disabled ?? false}
                    id={`tab-${navItem?.id}`}
                    key={`tab-${navItem?.id}`}
                    label={navItem?.title}
                    value={navItem?.itemKey}
                  />
                );
              })}
            </StyledTabsContainer>
          )}
        </StyledMenuItems>

        <StyledNavActions id="horizontal-nav-actions" item>
          {children}
        </StyledNavActions>
      </StyledContainer>
    </StyledPaper>
  );
};

export default HorizontalNavigation;
